import { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { Button } from '@helix-ds/react'
import { Redo } from 'flowbite-react-icons/outline'
import { RestartOrderModal, type RestartOrderMode } from '@/components/modals/RestartOrderModal'
import { RestartOrderStatusModal } from '@/components/modals/RestartOrderStatusModal'
import { RESTART_ORDER_TITLE } from '@/components/modals/restartOrderCopy'
import ProviderAPIClient from '@/features/auth/ProviderAPIClient'
import { AuthorizationContext } from '@/features/authorization/authorizationHooks'
import { RestartConflictError, RestartDraftExistsError } from '@/features/ordering/errors'
import { useAppDispatch } from '@/store/hooks'
import { resetCanonicalOrder } from '@/store/slices/canonicalOrderSlice'
import { getOrderRestartEligibility, type OrderRestartEligibility } from '@/utils/DatabaseOperations'
import { resolveImdbOrgIdForOrderingApi } from '@/utils/orderingOrgId'

export interface RestartOrderActionProps {
  orderId: string | undefined
  isCancelled: boolean
  authFlow: 'epic' | 'standalone'
  epicState: string | null
  orgId: string | null
  orderOrgUuidCandidate?: string
}

/**
 * Patient-header "Restart Order" action for a cancelled order (GEN-6020).
 *
 * Shown for every cancelled order when the user may create orders. Orders that
 * have been superseded, or whose patient already has a draft, explain why they
 * cannot be restarted instead of silently hiding or redirecting the action.
 */
export function RestartOrderAction({
  orderId,
  isCancelled,
  authFlow,
  epicState,
  orgId,
  orderOrgUuidCandidate,
}: RestartOrderActionProps) {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const canCreate = useContext(AuthorizationContext)?.hasPermission('order:create') === true
  const [eligibility, setEligibility] = useState<OrderRestartEligibility | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [statusModal, setStatusModal] = useState<'draft' | 'newer' | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setEligibility(null)
    if (!orderId || !isCancelled || !canCreate) return
    const controller = new AbortController()
    getOrderRestartEligibility(orderId, controller.signal)
      .then(setEligibility)
      .catch((e: unknown) => {
        if (!controller.signal.aborted) Sentry.captureException(e, { tags: { operation: 'order.restart.eligibility' } })
      })
    return () => controller.abort()
  }, [orderId, isCancelled, canCreate])

  if (!orderId || !isCancelled || !canCreate || !eligibility) {
    return null
  }

  const openDraft = (sessionId: string) => {
    dispatch(resetCanonicalOrder())
    navigate(`/order/start?sessionId=${encodeURIComponent(sessionId)}`, {
      state: { authFlow, ...(epicState ? { epicState } : {}) },
    })
  }

  const showBlockingStatus = (current: OrderRestartEligibility): boolean => {
    if (current.existingDraftSessionId) {
      setStatusModal('draft')
      return true
    }
    if (!current.isLatestOrder) {
      setStatusModal('newer')
      return true
    }
    return false
  }

  const handleClick = () => {
    if (showBlockingStatus(eligibility)) return
    setError(null)
    setModalOpen(true)
  }

  const handleChoose = (mode: RestartOrderMode) =>
    Sentry.startSpan({ op: 'ui.click', name: 'Restart Order' }, async () => {
      setIsSubmitting(true)
      setError(null)
      try {
        const organizationId = await resolveImdbOrgIdForOrderingApi({
          orgIdFromAuth: orgId,
          additionalUuidCandidate: orderOrgUuidCandidate,
          isEpicFlow: authFlow === 'epic',
          epicOAuthState: epicState,
        })
        if (!organizationId) throw new Error('Restart: organization could not be resolved')
        const result = await ProviderAPIClient.getInstance().createOrderingSession({
          organizationId,
          restartFromOrderId: orderId,
          restartMode: mode,
        })
        setModalOpen(false)
        openDraft(result['sessionId'] as string)
      } catch (e) {
        if (e instanceof RestartDraftExistsError) {
          setModalOpen(false)
          setEligibility((current) =>
            current ? { ...current, eligible: false, existingDraftSessionId: e.existingDraftSessionId } : current
          )
          setStatusModal('draft')
          return
        }
        if (e instanceof RestartConflictError) {
          try {
            const refreshed = await getOrderRestartEligibility(orderId)
            setEligibility(refreshed)
            setModalOpen(false)
            if (showBlockingStatus(refreshed)) return
          } catch (refreshError) {
            Sentry.captureException(refreshError, { tags: { operation: 'order.restart.eligibility.refresh' } })
          }
        }
        Sentry.captureException(e, { tags: { operation: 'order.restart.create' } })
        setModalOpen(false)
        setError("Couldn't restart this order. Try again.")
      } finally {
        setIsSubmitting(false)
      }
    })

  return (
    <>
      <Button type="button" variant="secondary" size="md" className="shadow-xs" icon={<Redo className="h-4 w-4" aria-hidden />} onClick={handleClick}>
        {RESTART_ORDER_TITLE}
      </Button>
      {error ? (
        <span role="alert" className="text-xs font-medium text-red-600">
          {error}
        </span>
      ) : null}
      <RestartOrderModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onChoose={handleChoose}
        cancelledAt={eligibility.cancelledAt}
        cancelledByName={eligibility.cancelledByName}
        isIntegratedTenant={authFlow === 'epic'}
        isSubmitting={isSubmitting}
      />
      <RestartOrderStatusModal
        isOpen={statusModal !== null}
        message={
          statusModal === 'draft'
            ? 'An order draft already exists for this patient.'
            : 'A newer order already exists for this patient. This canceled order cannot be restarted again.'
        }
        onClose={() => setStatusModal(null)}
      />
    </>
  )
}
